import React, { useState, useEffect, useMemo, useRef } from 'react';

import {
  Chip,
  BaseButton,
  HorizonLine,
  useAuth,
  useIsMobile,
  useT,
} from '@repo/common';
import QnaSearchInput from './QnaSearchInput';
import AskQuestionModal from '../Modal/AskQuestionModal';
import FaqModal from '../Modal/FaqModal';
import MobileSortFilter from '../Modal/MobileSortFilter';

import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';
import { ReactComponent as HumanIcon } from '@repo/common/assets/icons/28px/icon_28px_human.svg';

import { getQnaCategories } from '../../../api/qna/qnaCategory.api';
import { getSearchQna } from '../../../api/qna/qnaSearch.api';

const getCardStateClasses = (state) => {
  let baseClass = '';

  switch (state) {
    case 'card-pressed':
      baseClass = 'bg-green-light01 border-[6px] border-surface-green40';
      break;
    case 'card-answered':
      baseClass = 'bg-grayscale-gray00 opacity-60 text-text-primary';
      break;
    case 'card-default':
    default:
      baseClass = 'bg-grayscale-gray00';
      break;
  }
  return baseClass;
};

const QnaHeader = ({
  state: stateProp = 'card-default',
  activeCategory,
  setActiveCategory,
  isCategoryChangeRef,
  filterState,
  setFilterState,
}) => {
  const t = useT();
  const headerRef = useRef(null);
  const sentinelRef = useRef(null);
  const { user, isLoggedIn } = useAuth();
  const isMobile = useIsMobile();
  const [categories, setCategories] = useState([]);
  const [currentState, setCurrentState] = useState(stateProp);
  const [isSticky, setIsSticky] = useState(false);
  const [isFaqOpen, setIsFaqOpen] = useState(false);
  const [isAskModalOpen, setIsAskModalOpen] = useState(false);

  const UserAvatar = ({ isLoggedIn, user }) => {
    if (!isLoggedIn) {
      return (
        <HumanIcon className='w-[26px] h-[26px] text-grayscale-gray06 mr-[6px]' />
      );
    }

    if (user?.profileUrl) {
      return (
        <img
          src={user.profileUrl}
          alt='profile'
          className='w-[26px] h-[26px] rounded-full object-cover mr-[6px]'
        />
      );
    }

    return (
      <HumanIcon className='w-[26px] h-[26px] text-grayscale-gray06 mr-[6px]' />
    );
  };

  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
      },
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, []);

  const handleCategoryClick = (categoryId) => {
    isCategoryChangeRef.current = true;
    setActiveCategory(categoryId);
  };

  const currentCardClasses = useMemo(() => {
    return getCardStateClasses(currentState);
  }, [currentState]);

  const handleInteraction = (newState) => {
    setCurrentState(newState);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const data = await getQnaCategories({ includeAll: true });
      setCategories(data);

      if (!activeCategory) {
        const allCategory = data.find((c) => c.name === 'All');
        if (allCategory) {
          setActiveCategory(allCategory.id);
        }
      }
    };
    fetchCategories();
  }, [activeCategory, setActiveCategory]);

  const handleFilterChange = async (newState) => {
    setFilterState(newState);

    try {
      await getSearchQna({
        keyword: '',
        sortBy: newState.sortBy,
        onlyAnswered: newState.onlyAnswered,
        page: 0,
      });
    } catch (err) {
      console.error('QnA 정렬/필터 API 실패', err);
    }
  };

  return (
    <>
      <div ref={sentinelRef} className='h-[1px]' />
      {isSticky && <div className='tabletH:hidden h-[210px]' />}

      <div
        ref={headerRef}
        className={`
          w-full flex justify-center tabletH:pb-20
        `}
      >
        <div className='w-full border-none'>
          {/* Q&A 헤더 */}
          <div className='text-start tabletH:text-center mb-[30px] px-4 tabletH:p-0'>
            <div className='py-4 tabletH:p-0'>
              <p
                className={`typo-b18Strong font-semibold tabletH:typo-h2 text-green-deep04 tabletH:text-text-primary 
              inline-block
              px-1 bg-[linear-gradient(transparent,transparent_50%,rgba(0,158,79,0.4)_50%)]
              tracking-wide tabletH:bg-none`}
              >
                Q&A
              </p>
            </div>
            <p className={`typo-h4 tabletH:typo-b16Strong text-text-primary`}>
              {t(
                'Find answers or ask your own question',
                '답변을 찾아보거나 직접 질문해 보세요',
              )}
              <BaseButton
                type='text'
                size='s'
                hideIcon
                textclass='text-purple-purple04 underline active:text-text-accent'
                bgclass='active:bg-grayscale-gray01 tabletH:hidden'
                onClick={() => setIsFaqOpen(true)}
              >
                {t('See FAQs', 'FAQ 보기')}
              </BaseButton>
            </p>
            {isFaqOpen && (
              <FaqModal
                isOpen={isFaqOpen}
                onClose={() => setIsFaqOpen(false)}
              />
            )}
          </div>

          {/* 검색 */}
          <div className='flex justify-center mb-[50px] px-4 tabletH:mb-8 tabletH:px-0'>
            <div className='w-[570px]'>
              <QnaSearchInput enableNavigate />
            </div>
          </div>

          <HorizonLine className='tabletH:hidden' />

          {/* 카테고리 */}
          <div
            className='flex flex-nowrap gap-2 tabletH:max-h-[34px] tabletH:h-full
              overflow-x-auto scrollbar-none
              px-4 py-3 justify-start
              tabletH:justify-center tabletH:overflow-x-visible tabletH:p-0'
          >
            {categories.map((category) => {
              const isAdmin = category.id === 9;
              const isActive = activeCategory === category.id;

              let bgClass = '';
              if (isAdmin) {
                bgClass = isActive
                  ? 'bg-functional-accent03 text-grayscale-gray01'
                  : 'bg-grayscale-gray01 text-text-accent ';
              } else {
                bgClass = isActive
                  ? 'ring-grayscale-gray07 text-text-primary bg-transparent'
                  : 'ring-grayscale-gray02 bg-transparent text-text-secondary';
              }

              return (
                <Chip
                  key={category.id}
                  state={isActive ? 'selected' : 'default'}
                  type={isActive ? 'fill' : 'outline'}
                  size={isMobile ? 'l' : 'm'}
                  hideIcon
                  onClick={() => handleCategoryClick(category.id)}
                  disableTypography
                  bgclass={bgClass}
                  textclass='shrink-0 typo-b14Strong tabletH:typo-b12Strong px-4 py-3 tabletH:px-3 tabletH:py-2'
                >
                  {category.name}
                </Chip>
              );
            })}
          </div>

          {/* 모바일 전용 필터링 */}
          <MobileSortFilter
            isSticky={isSticky}
            onChange={handleFilterChange}
            filterState={filterState}
          />

          {/* 모바일 전용 질문 등록 */}
          <div className='tabletH:hidden p-4'>
            <div
              className={`h-[100px] bg-grayscale-gray00 rounded-[30px] border-[1.6px] border-grayscale-gray02 
               soft-70 p-6 flex ${currentCardClasses}`}
              onMouseEnter={() => handleInteraction('card-hover')}
              onClick={() => {
                setIsAskModalOpen(true);
              }}
            >
              <div className='flex flex-col justify-between w-full'>
                {/* 로그인 전 */}
                <div className='flex items-center pb-1 typo-b14Strong text-grayscale-gray09'>
                  <UserAvatar isLoggedIn={isLoggedIn} user={user} />
                  {isLoggedIn ? (
                    <span>{user?.nickname || `${user?.firstName}`} </span>
                  ) : (
                    <span>
                      {t('You’re not logged in.', '로그인되어 있지 않습니다.')}
                    </span>
                  )}
                </div>

                <div className='typo-14 text-text-placeholder ml-[30px]'>
                  {t('Ask us? Anything!', '무엇이든 물어보세요!')}
                </div>
              </div>
              <BaseButton
                type='text'
                size='xl'
                icon={<PlusIcon />}
                bgclass='bg-transform border-none'
              />
            </div>
          </div>

          <div
            className={`tabletH:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-50 ${
              isSticky
                ? 'translate-y-0 opacity-100'
                : 'translate-y-10 opacity-0 pointer-events-none'
            }`}
          >
            <BaseButton
              size='xl'
              bgclass='w-[185px] rounded-[50px] shadow-popup-01'
              onClick={() => setIsAskModalOpen(true)}
            >
              {t('New Question', '새 질문')}
            </BaseButton>
          </div>
          {isAskModalOpen && (
            <AskQuestionModal
              isOpen={isAskModalOpen}
              onClose={() => setIsAskModalOpen(false)}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default QnaHeader;
